import { useState, useEffect } from 'react';

const SIZES = {
  // `mini` fills whatever box it is given — for dense admin tables where the
  // container is already sized and padding would only shrink the initial.
  mini: { circle: 'w-full h-full text-sm', label: '', gap: '', showLabel: false },
  // `tile` is for list rows where the brand name already sits beside the image,
  // so repeating it under the initial would just be the same word twice.
  tile: { circle: 'w-10 h-10 text-lg', label: 'text-[10px]', gap: 'gap-1 p-2', showLabel: false },
  card: { circle: 'w-16 h-16 text-3xl', label: 'text-xs', gap: 'gap-2 p-4', showLabel: true },
  hero: { circle: 'w-32 h-32 text-6xl', label: 'text-base', gap: 'gap-4 p-8', showLabel: true },
  // `plate` is `card` on a caller-supplied background — the gift-card tile paints
  // its own brand tint, and a grey fallback box would cover it.
  plate: { circle: 'w-14 h-14 text-2xl', label: 'text-[11px]', gap: 'gap-2 p-4', showLabel: true, bg: 'bg-transparent' },
  // The grid tile is ~90px tall and already prints the brand name underneath, so
  // the label here was both redundant and taller than the box — which is what cut
  // "DECATHLON" and "Me n Moms" in half against the GIFT CARD strip.
  plateSm: { circle: 'w-9 h-9 text-base', label: '', gap: 'p-2', showLabel: false, bg: 'bg-transparent' },
};

/**
 * Renders the first gift-card image that actually loads.
 *
 * Roughly half the Razorpay catalogue has no usable image at all (see
 * getRewardImageCandidates), so the terminal state is a branded initial tile
 * rather than the generic "No image" placeholder — a lettered Khadims tile reads
 * as a deliberate design, a broken-image box reads as a broken store.
 */
export function RewardImage({ candidates = [], name, size = 'card', className = '' }) {
  const [index, setIndex] = useState(0);

  // Reset when the reward changes. Without this an exhausted chain from the
  // previous reward keeps the placeholder pinned on the next one, because the
  // component is reused across cards in the same list position.
  const chain = candidates.join('|');
  useEffect(() => { setIndex(0); }, [chain]);

  const src = candidates[index];
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const s = SIZES[size] || SIZES.card;

  // className describes how to fit an <img> (object-contain, hover scale, …) and
  // means nothing to the tile, so it is deliberately not forwarded here.
  if (!src) {
    return (
      <div className={`w-full h-full flex flex-col items-center justify-center ${s.bg || 'bg-surface'} ${s.gap}`}>
        <span className={`flex items-center justify-center rounded-full bg-primary/10 text-primary font-heading ${s.circle}`}>
          {initial}
        </span>
        {s.showLabel && (
          <span className={`${s.label} text-muted-foreground font-body text-center line-clamp-2`}>
            {name}
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      onError={() => setIndex((i) => i + 1)}
      className={className}
      loading="lazy"
    />
  );
}
